/**
 * Redux duck for managing global mockup upload state
 * This allows upload progress to persist across page navigation
 */

// ================ Action types ================ //

export const START_MOCKUP_UPLOAD = 'app/mockupUpload/START_MOCKUP_UPLOAD';
export const UPDATE_UPLOAD_STATUS = 'app/mockupUpload/UPDATE_UPLOAD_STATUS';
export const COMPLETE_MOCKUP_UPLOAD = 'app/mockupUpload/COMPLETE_MOCKUP_UPLOAD';
export const FAIL_MOCKUP_UPLOAD = 'app/mockupUpload/FAIL_MOCKUP_UPLOAD';
export const CLEAR_MOCKUP_UPLOAD = 'app/mockupUpload/CLEAR_MOCKUP_UPLOAD';
export const DISMISS_MOCKUP_BANNER = 'app/mockupUpload/DISMISS_MOCKUP_BANNER';
export const TRIGGER_MOCKUP_POLLING = 'app/mockupUpload/TRIGGER_MOCKUP_POLLING';
export const SET_UPLOADED_IMAGE = 'app/mockupUpload/SET_UPLOADED_IMAGE';
export const UPDATE_UPLOAD_PROGRESS = 'app/mockupUpload/UPDATE_UPLOAD_PROGRESS';

// ================ Reducer ================ //

const initialState = {
  isUploading: false,
  isDismissed: false,
  currentJobId: null,
  progressStatus: null, // 'pending', 'ai-processing', 'uploading', 'segmenting', 'completed', 'failed'
  error: null,
  listingContext: null, // { listingId, listingSlug, listingTitle }
  completedAt: null,
  pollingTriggeredAt: null, // Timestamp to trigger polling in MockupPollManager
  // Streaming partial image fields
  uploadedImageDataUrl: null, // User's raw uploaded image (shown initially with heavy blur)
  partialImageUrl: null, // URL to fetch current partial image from server
  partialIndex: -1, // -1 = showing uploaded image, 0-2 = showing partial
};

export default function mockupUploadReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case START_MOCKUP_UPLOAD:
      return {
        ...state,
        isUploading: true,
        isDismissed: false,
        currentJobId: payload.jobId,
        progressStatus: 'pending',
        error: null,
        listingContext: payload.listingContext,
        completedAt: null,
      };

    case UPDATE_UPLOAD_STATUS:
      return {
        ...state,
        progressStatus: payload.status,
      };

    case COMPLETE_MOCKUP_UPLOAD:
      return {
        ...state,
        isUploading: false,
        progressStatus: 'completed',
        completedAt: Date.now(),
      };

    case FAIL_MOCKUP_UPLOAD:
      return {
        ...state,
        isUploading: false,
        progressStatus: 'failed',
        error: payload.error,
      };

    case DISMISS_MOCKUP_BANNER:
      return {
        ...state,
        isDismissed: true,
      };

    case CLEAR_MOCKUP_UPLOAD:
      return initialState;

    case TRIGGER_MOCKUP_POLLING:
      return {
        ...state,
        pollingTriggeredAt: Date.now(),
      };

    case SET_UPLOADED_IMAGE:
      return {
        ...state,
        uploadedImageDataUrl: payload.dataUrl,
      };

    case UPDATE_UPLOAD_PROGRESS:
      return {
        ...state,
        progressStatus: payload.status,
        partialImageUrl: payload.partialImageUrl,
        partialIndex: payload.partialIndex,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const startMockupUpload = (jobId, listingContext) => ({
  type: START_MOCKUP_UPLOAD,
  payload: { jobId, listingContext },
});

export const updateUploadStatus = status => ({
  type: UPDATE_UPLOAD_STATUS,
  payload: { status },
});

export const completeMockupUpload = () => ({
  type: COMPLETE_MOCKUP_UPLOAD,
});

export const failMockupUpload = error => ({
  type: FAIL_MOCKUP_UPLOAD,
  payload: { error },
});

export const dismissMockupBanner = () => ({
  type: DISMISS_MOCKUP_BANNER,
});

export const clearMockupUpload = () => ({
  type: CLEAR_MOCKUP_UPLOAD,
});

export const triggerMockupPolling = jobId => ({
  type: TRIGGER_MOCKUP_POLLING,
  payload: { jobId },
});

export const setUploadedImage = dataUrl => ({
  type: SET_UPLOADED_IMAGE,
  payload: { dataUrl },
});

export const updateUploadProgress = (status, partialData = {}) => ({
  type: UPDATE_UPLOAD_PROGRESS,
  payload: {
    status,
    partialImageUrl: partialData.partialImageUrl || null,
    partialIndex: partialData.partialIndex ?? -1,
  },
});

// ================ Selectors ================ //

export const selectMockupUpload = state => state.mockupUpload;

export const selectIsUploading = state => state.mockupUpload.isUploading;

export const selectShouldShowBanner = state => {
  const { isUploading, isDismissed, progressStatus } = state.mockupUpload;
  // Show banner if uploading or if completed/failed and not dismissed
  return (isUploading || progressStatus === 'completed' || progressStatus === 'failed') && !isDismissed;
};

export const selectListingContext = state => state.mockupUpload.listingContext;

/**
 * Selector for preview image during upload
 * Returns the appropriate image and blur level based on current state
 * @returns {{ url: string, blur: number } | null}
 */
export const selectPreviewImage = state => {
  const { uploadedImageDataUrl, partialImageUrl, partialIndex } = state.mockupUpload;

  // If we have a partial image URL, show it with decreasing blur
  if (partialIndex >= 0 && partialImageUrl) {
    const blurLevels = [16, 8, 4]; // Blur decreases as partials arrive
    return {
      url: partialImageUrl,
      blur: blurLevels[partialIndex] ?? 4,
    };
  }

  // Otherwise show the uploaded image with heavy blur
  if (uploadedImageDataUrl) {
    return {
      url: uploadedImageDataUrl,
      blur: 20,
    };
  }

  return null;
};
